import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import {
  FaChartBar, FaCalendarAlt, FaQrcode, FaUser,
  FaChartLine, FaBullhorn, FaSignOutAlt, FaBars,
  FaTimes, FaUtensils, FaUsers, FaLock, FaBox, FaMapMarkerAlt, FaChevronDown,
  FaTh, FaEllipsisH, FaStar, FaStore,
} from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import { useLocation } from '../../context/LocationContext';
import '../../styles/Sidebar.css';

const TIER_NAMES  = { 1: 'Starter', 2: 'Growth', 3: 'Premium' };
const TIER_COLORS = { 1: '#6B7280', 2: '#f97316', 3: '#8B5CF6' };

const MENU_ITEMS = [
  { path: '/dashboard',  icon: <FaChartBar />,    label: 'Dashboard',      minTier: 1 },
  { path: '/analytics',  icon: <FaChartLine />,   label: 'Hesabatlar',     minTier: 1 },
  { path: '/menu',       icon: <FaUtensils />,    label: 'Menyu',          minTier: 1 },
  { path: '/floor-plan', icon: <FaTh />,           label: 'Floor Plan',     minTier: 2 },
  { path: '/bookings',   icon: <FaCalendarAlt />, label: 'Rezervasiyalar', minTier: 2 },
  { path: '/campaigns',  icon: <FaBullhorn />,    label: 'Kampaniyalar',   minTier: 2 },
  { path: '/scanner',    icon: <FaQrcode />,        label: 'QR Skan',        minTier: 2 },
  { path: '/locations',  icon: <FaMapMarkerAlt />,    label: 'Filiallar',      minTier: 3 },
  { path: '/reviews',    icon: <FaStar />,             label: 'Rəylər',         minTier: 1 },
  { path: '/profile',    icon: <FaUser />,             label: 'Profil',         minTier: 1 },
];

// Filial şəbəkəsi sahibi üçün yalnız ümumi idarəetmə menüsu
const CHAIN_OWNER_ITEMS = [
  { path: '/chain-overview', icon: <FaStore />,        label: 'Filial İcmalı' },
  { path: '/dashboard',      icon: <FaChartBar />,     label: 'Dashboard' },
  { path: '/analytics',      icon: <FaChartLine />,    label: 'Hesabatlar' },
  { path: '/locations',      icon: <FaMapMarkerAlt />, label: 'Filiallar' },
  { path: '/profile',        icon: <FaUser />,          label: 'Profil' },
];

// Bottom nav — 5 primary items shown on mobile
const BOTTOM_NAV = [
  { path: '/dashboard',  icon: <FaChartBar />,    label: 'Dashboard' },
  { path: '/bookings',   icon: <FaCalendarAlt />, label: 'Rezerv' },
  { path: '/scanner',    icon: <FaQrcode />,      label: 'QR Skan' },
  { path: '/menu',       icon: <FaUtensils />,    label: 'Menyu' },
  { path: '/profile',    icon: <FaUser />,        label: 'Profil' },
];

const Sidebar = () => {
  const { business, logout } = useAuth();
  const { locations, activeLocation, setActiveLocation, isOwner, userRole } = useLocation();
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [locDropdownOpen, setLocDropdownOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  const currentTier = business?.premium_tier || 1;
  const tierColor   = TIER_COLORS[currentTier] || '#6B7280';
  const tierName    = TIER_NAMES[currentTier]  || 'Starter';
  const isChainOwner = isOwner && !!business?.is_chain;
  const visibleItems = isChainOwner ? CHAIN_OWNER_ITEMS : MENU_ITEMS.filter(item => item.path !== '/locations');

  const toggleSidebar = () => setIsOpen(p => !p);
  const closeSidebar  = () => setIsOpen(false);

  return (
    <>
      <button className="hamburger-button" onClick={toggleSidebar}>
        {isOpen ? <FaTimes /> : <FaBars />}
      </button>

      {isOpen && <div className="sidebar-overlay" onClick={closeSidebar}></div>}

      <div className={`sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h2>☕ Cafegoo</h2>
          <p>{business?.name}</p>

          {/* Active plan badge — click to change (owner only) */}
          {isOwner && (
            <div
              onClick={() => { navigate('/select-package'); closeSidebar(); }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                marginTop: 8, padding: '3px 10px', borderRadius: 20,
                background: tierColor + '22',
                border: `1px solid ${tierColor}55`,
                color: tierColor, fontSize: 11, fontWeight: 700,
                cursor: 'pointer', letterSpacing: 0.5,
              }}
              title="Planı dəyişdir"
            >
              <FaBox style={{ fontSize: 9 }} />
              {tierName} planı
            </div>
          )}

          {/* Manager role badge */}
          {!isOwner && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              marginTop: 8, padding: '3px 10px', borderRadius: 20,
              background: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.25)',
              color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
            }}>
              👤 {userRole === 'manager' ? 'Menecer' : 'Staff'}
            </div>
          )}

          {/* Location switcher — only if owner with multiple locations */}
          {isOwner && locations.length > 0 && (
            <div style={{ marginTop: 10, position: 'relative' }}>
              <div
                onClick={() => setLocDropdownOpen(p => !p)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '6px 10px', borderRadius: 8,
                  background: 'rgba(255,255,255,0.12)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  color: '#fff',
                }}
              >
                <FaMapMarkerAlt style={{ fontSize: 10, flexShrink: 0 }} />
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {activeLocation?.name || 'Filial seçin'}
                </span>
                <FaChevronDown style={{ fontSize: 9, flexShrink: 0, transform: locDropdownOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
              </div>
              {locDropdownOpen && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 2000,
                  background: '#fff', borderRadius: 8, marginTop: 4,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                  overflow: 'hidden', maxHeight: 200, overflowY: 'auto',
                }}>
                  {locations.map(loc => (
                    <div
                      key={loc.id}
                      onClick={() => { setActiveLocation(loc); setLocDropdownOpen(false); }}
                      style={{
                        padding: '9px 12px', fontSize: 12, cursor: 'pointer',
                        color: activeLocation?.id === loc.id ? '#f97316' : '#333',
                        fontWeight: activeLocation?.id === loc.id ? 700 : 400,
                        background: activeLocation?.id === loc.id ? '#fff7ed' : '#fff',
                        borderBottom: '1px solid #f5f5f5',
                      }}
                    >
                      {loc.is_main ? '⭐ ' : '📍 '}{loc.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <nav className="sidebar-menu">
          {visibleItems.map((item) => {
            const locked = !isChainOwner && item.minTier && currentTier < item.minTier;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => isActive ? 'menu-item active' : 'menu-item'}
                onClick={closeSidebar}
                style={locked ? { opacity: 0.5 } : {}}
              >
                <span className="menu-icon">{item.icon}</span>
                <span className="menu-label" style={{ flex: 1 }}>{item.label}</span>
                {locked && (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 3,
                    fontSize: 10, fontWeight: 700,
                    color: '#f97316',
                    background: '#f9731618',
                    border: '1px solid #f9731630',
                    borderRadius: 6,
                    padding: '1px 6px',
                    flexShrink: 0,
                  }}>
                    <FaLock style={{ fontSize: 8 }} />
                    {TIER_NAMES[item.minTier]}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <button className="logout-button" onClick={logout}>
          <FaSignOutAlt />
          <span>Çıxış</span>
        </button>
      </div>

      {/* ── Bottom Nav (mobile only) ── */}
      <nav className="bottom-nav">
        {(isChainOwner ? CHAIN_OWNER_ITEMS : BOTTOM_NAV).map(item => {
          const isActive = routerLocation.pathname === item.path;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={`bn-item${isActive ? ' active' : ''}`}
              onClick={() => { closeSidebar(); setMoreOpen(false); }}
            >
              <span className="bn-icon">{item.icon}</span>
              <span>{item.label}</span>
            </NavLink>
          );
        })}
        <button
          className={`bn-more${moreOpen ? ' open' : ''}`}
          onClick={() => { setMoreOpen(p => !p); closeSidebar(); }}
        >
          <span className="bn-icon"><FaEllipsisH /></span>
          <span>Daha çox</span>
        </button>
      </nav>

      {/* ── "More" drawer (mobile) ── */}
      {moreOpen && (
        <>
          <div className="sidebar-overlay" onClick={() => setMoreOpen(false)} />
          <div className="more-drawer">
            {visibleItems.filter(i => !(isChainOwner ? CHAIN_OWNER_ITEMS : BOTTOM_NAV).find(b => b.path === i.path)).map(item => {
              const locked = !isChainOwner && item.minTier && currentTier < item.minTier;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) => `more-item${isActive ? ' active' : ''}`}
                  onClick={() => setMoreOpen(false)}
                  style={locked ? { opacity: 0.5 } : {}}
                >
                  <span className="more-icon">{item.icon}</span>
                  <span className="more-label">{item.label}</span>
                  {locked && (
                    <span style={{ fontSize: 10, color: '#f97316', fontWeight: 700, marginLeft: 'auto' }}>
                      <FaLock style={{ fontSize: 8 }} /> {TIER_NAMES[item.minTier]}
                    </span>
                  )}
                </NavLink>
              );
            })}
            <button
              className="more-logout"
              onClick={() => { setMoreOpen(false); logout(); }}
            >
              <FaSignOutAlt /> Çıxış
            </button>
          </div>
        </>
      )}
    </>
  );
};

export default Sidebar;
